import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { PALS, ELEMENT_COLORS, type Pal } from "@/lib/pals-data";
import { PalPortrait } from "@/components/PalPortrait";
import { FaqSection, RelatedGuides, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { SNIPPETS } from "@/lib/snippet-copy";
import { ContextVisualStrip } from "@/components/ContextVisualStrip";
import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";

const FAQ: FaqItem[] = [
  {
    q: "What is the easiest Pal to capture in Palworld?",
    a: "Lamball, Cattiva and Chikipi have the highest base capture rates in Palworld, so a Basic Pal Sphere catches them reliably even at full HP. Anything above a 60% base rate on this table is safe to throw at without weakening it first.",
  },
  {
    q: "How is the Palworld capture rate calculated?",
    a: "Final catch chance = Base Rate x Sphere Multiplier x HP Factor x Sneak Bonus. Dropping a Pal below 30% HP roughly doubles the HP Factor, and a back-attack sneak throw adds a further bonus, so a 10% base-rate Pal can reach a realistic 40-50% per throw.",
  },
  {
    q: "Which Pal Sphere should I use for each capture rate tier?",
    a: "Use a Basic Sphere at 60% or higher, a Mega Sphere from 40-59%, a Giga Sphere from 25-39%, a Hyper Sphere from 12-24%, an Ultra Sphere from 6-11%, and a Legendary Sphere below 6%. Going one tier above the recommendation is the fastest way to save time on rare Pals.",
  },
  {
    q: "Can you capture legendary Pals like Jetragon and Bellanoir?",
    a: "Yes. Legendary Pals have single-digit base capture rates, so bring at least 20 Legendary Spheres, weaken them below 20% HP and apply status effects before throwing. Expect several attempts even with perfect conditions.",
  },
  {
    q: "Does capture rate affect Pal stats or work speed?",
    a: "No. Capture rate only affects how hard a Pal is to catch. Stats, passive skills and work suitability are rolled independently, so a re-caught Pal can have better passives than your first one.",
  },
  {
    q: "Can I use the Palworld catch rate calculator on mobile?",
    a: "Yes. The mobile Pal catch rate calculator and full capture table work in a phone browser with the same Pal filters, sphere recommendations and catch-chance formula as desktop.",
  },
];

export const Route = createFileRoute("/capture-rates")({
  head: () => ({
    meta: [
      { title: "Palworld Capture Rate Table — Every Pal Ranked | Pal Stars" },
       { name: "description", content: "Palworld capture rates and mobile Pal catch rate calculator guidance: compare catch chance and sphere tiers for every Pal." },
      { property: "og:title", content: "Palworld Capture Rate Table — Pal Stars" },
      { property: "og:description", content: "Catch chances and recommended sphere tier for every Pal in Palworld." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://palstars.com/capture-rates" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Palworld Capture Rate Table — Pal Stars" },
        { name: "twitter:description", content: "Catch chances and recommended sphere tier for every Pal in Palworld." },
    ],
    links: [{ rel: "canonical", href: "https://palstars.com/capture-rates" }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Capture Rates", "/capture-rates"]]),
{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Dataset",
        name: "Palworld Capture Rate Table",
        description: "Capture rate, sphere tier and food requirements for every Pal.",
        url: "https://palstars.com/capture-rates",
        creator: { "@type": "Organization", name: "Pal Stars", url: "https://palstars.com" },
        license: "https://creativecommons.org/licenses/by-nc/4.0/",
        isAccessibleForFree: true,
      }),
    },
    faqSchema(FAQ, "https://palstars.com/capture-rates"),
    snippetAnswerSchema({ question: SNIPPETS.capture.question, answer: SNIPPETS.capture.answer, path: "/capture-rates" }),
    ],
  }),
  component: CapturePage,
});


function sphereTier(rate: number): { name: string; color: string } {
  if (rate >= 60) return { name: "Basic", color: "#94A3B8" };
  if (rate >= 40) return { name: "Mega", color: "#22C55E" };
  if (rate >= 25) return { name: "Giga", color: "#38BDF8" };
  if (rate >= 12) return { name: "Hyper", color: "#8B5CF6" };
  if (rate >= 6) return { name: "Ultra", color: "#F97316" };
  return { name: "Legendary", color: "#EF4444" };
}

function CapturePage() {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<"rate-desc"|"rate-asc"|"name">("rate-desc");

  const rows = useMemo(() => {
    const filtered = PALS.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
    const sorted = filtered.slice().sort((a, b) => {
      if (sort === "name") return a.name.localeCompare(b.name);
      if (sort === "rate-asc") return a.captureRate - b.captureRate;
      return b.captureRate - a.captureRate;
    });
    return sorted;
  }, [q, sort]);

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10">
      <nav className="text-xs text-muted-foreground mb-4">
        <Link to="/" className="hover:text-foreground">Home</Link> / <span className="text-foreground">Capture rates</span>
      </nav>
      <h1 className="text-4xl font-black">Palworld capture rate table</h1>
      <p className="mt-2 text-muted-foreground max-w-2xl">
        Every Pal ranked by base catch chance, recommended sphere tier and daily food cost. Higher rate = easier to capture with a standard sphere at full HP.
      </p>
      <p className="aeo-answer mt-3 max-w-2xl text-sm text-muted-foreground">
        This mobile Pal catch rate calculator works directly in your phone browser: search a Pal, compare its base catch chance, then use the recommended sphere tier before your next throw.
      </p>

      <SnippetFunnel className="mt-6" {...SNIPPETS.capture} />
      <ContextVisualStrip variant="capture" />

      <div className="mt-6 flex flex-wrap gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name…"
          aria-label="Filter capture rates by Pal name"
          className="flex-1 min-w-[200px] rounded-lg glass px-4 py-2 outline-none focus:ring-2 ring-primary text-sm"
        />
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
          aria-label="Sort capture rates"
          className="rounded-lg glass px-3 py-2 text-sm outline-none"
        >
          <option value="rate-desc">Rate: high → low</option>
          <option value="rate-asc">Rate: low → high</option>
          <option value="name">Name A–Z</option>
        </select>
      </div>

      {/* Semantic table: crawlers read a real <table> with headers, not a div grid. */}
      <div className="mt-6 glass rounded-xl overflow-hidden">
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full min-w-[720px] text-sm border-collapse">
            <caption className="sr-only">
              Palworld capture rate table: base catch chance, recommended Pal Sphere tier and daily food cost for every Pal.
            </caption>
            <thead className="sticky top-0 bg-background/90 backdrop-blur">
              <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                <th scope="col" className="px-4 py-2 text-left font-medium">Pal</th>
                <th scope="col" className="px-2 py-2 text-left font-medium">Element</th>
                <th scope="col" className="px-2 py-2 text-left font-medium">Capture rate</th>
                <th scope="col" className="px-2 py-2 text-left font-medium">Recommended sphere</th>
                <th scope="col" className="px-4 py-2 text-left font-medium">Food</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((p: Pal) => {
                const tier = sphereTier(p.captureRate);
                return (
                  <tr key={p.id} className="hover:bg-card align-middle">
                    <th scope="row" className="px-4 py-2 text-left font-normal">
                      <Link to="/pals/$slug" params={{ slug: p.slug }} className="flex items-center gap-2 min-w-0">
                        <span className="h-10 w-10 shrink-0 rounded-md overflow-hidden bg-background/50 block">
                          <PalPortrait pal={p} size={40} />
                        </span>
                        <span className="min-w-0">
                          <span className="font-medium block truncate">{p.name}</span>
                          <span className="text-[10px] text-muted-foreground block">
                            <span className="font-mono">{p.tcg.cardNumber}</span> · {p.rarity}
                          </span>
                        </span>
                      </Link>
                    </th>
                    <td className="px-2 py-2">
                      <span
                        className="text-xs px-2 py-0.5 rounded-full inline-block w-fit"
                        style={{ background: ELEMENT_COLORS[p.element] + "22", color: ELEMENT_COLORS[p.element] }}
                      >
                        {p.element}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className="flex items-center gap-2">
                        <span className="h-1.5 w-14 rounded-full bg-background/60 overflow-hidden block">
                          <span className="h-full bg-gradient-to-r from-primary to-secondary block" style={{ width: `${p.captureRate}%` }} />
                        </span>
                        <span className="font-semibold">{p.captureRate}%</span>
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full inline-block w-fit" style={{ background: tier.color + "22", color: tier.color }}>
                        {tier.name} sphere
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{p.foodRequirement} slots/day</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>


      <div className="glass rounded-xl p-6 mt-8 text-sm text-muted-foreground">
        <h2 className="text-base font-semibold text-foreground mb-2">How the capture rate is calculated</h2>
        <p className="aeo-answer">Final catch chance = <strong>Base Rate × Sphere Multiplier × HP Factor × Sneak Bonus</strong>. Weaken a Pal below 30% HP and use the sphere tier one step above the recommended tier for the highest odds. Legendaries always require multiple attempts — bring at least 20 Legendary Spheres before engaging a Jetragon or Bellanoir.</p>
      </div>

      <CrossCategoryLinks side="game" />

      <FaqSection items={FAQ} heading="Palworld capture rate FAQ" />
      <RelatedGuides
        links={[
          { slug: "pal-sphere-guide", label: "Pal Sphere Guide & FAQ" },
          { slug: "popular-pals-capture-guide", label: "Popular Pals Capture Guide" },
          { slug: "human-workers-capture-guide", label: "How to Capture Humans as Workers" },
          { slug: "palworld-breeding-guide", label: "Palworld Breeding Guide" },
        ]}
      />
    </div>
  );
}

